import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiZap } from 'react-icons/fi';
import DowntimeRecordsList from './DowntimeRecordsList';
import { authenticatedFetch } from '../utils/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const HeaderBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 16px 32px;
  box-shadow: none;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  gap: 16px;
  z-index: 1;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  grid-column: 1;
  min-width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  white-space: nowrap;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h2`
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(208, 215, 222, 0.8);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldLabel = styled.div`
  color: #333333;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 0.75rem;
`;

const PickerRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Card = styled.div`
  padding: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #333333;
  font-weight: 500;
  font-size: 0.8rem;
  background: white;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const SubmitBar = styled.div`
  display: flex;
  justify-content: center;
`;

const SubmitButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #333333;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SegmentedToggle = styled.div`
  display: inline-flex;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  overflow: hidden;
  height: 28px;
`;

const SegmentedOption = styled.button`
  background: ${props => props.$active ? '#333333' : 'white'};
  color: ${props => props.$active ? 'white' : '#333333'};
  border: none;
  padding: 3px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? '#333333' : '#f5f5f5'};
  }
`;


const DowntimeReportPage = ({ plant, onBack, onSubmit }) => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  const [startDate, setStartDate] = useState(todayStr);
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(todayStr);
  const [endTime, setEndTime] = useState('00:15');
  const [timezone, setTimezone] = useState('UTC'); // 'UTC', 'CET', or 'EET'
  const [invertersDown, setInvertersDown] = useState(null);
  const [maxInverters, setMaxInverters] = useState(0);
  const [loadingInverters, setLoadingInverters] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchMaxInverters = async () => {
      try {
        const resp = await authenticatedFetch(`/api/plants/${plant.id}/inverters/max`);
        const data = await resp.json();
        if (data && data.success) {
          setMaxInverters(Number(data.maxInverters || 0));
        }
      } catch (error) {
        // Failed to fetch inverter count
        setMaxInverters(0);
      } finally {
        setLoadingInverters(false);
      }
    };

    fetchMaxInverters();
  }, [plant.id]);

  const fetchRecords = async () => {
    // Refresh the DowntimeRecordsList component via refreshKey
    setRefreshKey((k) => k + 1);
  };

  useEffect(() => { fetchRecords(); }, [plant.id]);


  const handleSubmit = async () => {
    if (!startDate || !startTime || !endDate || !endTime) return;
    
    // Convert from selected timezone to UTC
    const toUtcString = (dateStr, timeStr, tz) => {
      const [y, m, d] = dateStr.split('-').map(Number);
      const [hh, mm] = timeStr.split(':').map(Number);
      
      // Calculate UTC offset
      let offsetHours = 0;
      if (tz === 'CET') {
        offsetHours = -1; // CET is UTC+1, so subtract 1 to get UTC
      } else if (tz === 'EET') {
        offsetHours = -2; // EET is UTC+2, so subtract 2 to get UTC
      }
      // UTC: offsetHours = 0
      
      // Create a date object representing the time in the selected timezone
      // We'll treat the input as if it's in that timezone and convert to UTC
      const dateInTz = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
      
      // Apply the offset to get UTC time
      const utcDate = new Date(dateInTz.getTime() + (offsetHours * 60 * 60 * 1000));
      
      const pad = (n) => String(n).padStart(2, '0');
      return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())} ${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:00`;
    };

    const startUtc = toUtcString(startDate, startTime, timezone);
    const endUtc = toUtcString(endDate, endTime, timezone);

    // Calculate inverters running from inverters down for CSV
    const invertersRunning = maxInverters > 0 && invertersDown !== null 
      ? Math.max(0, maxInverters - invertersDown) 
      : 0;

    const body = {
      start: startUtc,
      end: endUtc,
      invertersRunning: invertersRunning
    };

    try {
      const resp = await authenticatedFetch(`/api/plants/${plant.id}/inverters/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        return;
      }
      // Show browser alert instead of page message
      alert('Downtime record added successfully');
      // Refresh in-place: re-fetch records and keep user on this page
      await fetchRecords();
      // Signal record list to refresh
      setRefreshKey((k) => k + 1);
    } catch (e) {
      // Error handled silently
    }
  };


  // Validation: end must be >= start (in local time)
  const getLocalDateFromStrings = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  };
  const startLocal = getLocalDateFromStrings(startDate, startTime);
  const endLocal = getLocalDateFromStrings(endDate, endTime);
  const invalidRange = !!(startLocal && endLocal && endLocal.getTime() <= startLocal.getTime());


  const timeSlots = useMemo(() => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const HH = String(h).padStart(2, '0');
        const MM = String(m).padStart(2, '0');
        slots.push(`${HH}:${MM}`);
      }
    }
    return slots;
  }, []);

  const now = useMemo(() => new Date(), []);
  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
  }, [now]);
  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: new Date(2000, i, 1).toLocaleString(undefined, { month: 'long' })
    }));
  }, []);

  const getParts = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
    if (!y || !m || !d) return null;
    return { y, m, d };
  };

  const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

  const setStartParts = (y, m, d) => {
    const yy = y ?? (getParts(startDate)?.y ?? now.getFullYear());
    const mm = m ?? (getParts(startDate)?.m ?? now.getMonth() + 1);
    const dim = daysInMonth(yy, mm);
    const dd = Math.min(d ?? (getParts(startDate)?.d ?? now.getDate()), dim);
    const pad = (n) => String(n).padStart(2, '0');
    setStartDate(`${yy}-${pad(mm)}-${pad(dd)}`);
  };

  const setEndParts = (y, m, d) => {
    const yy = y ?? (getParts(endDate)?.y ?? now.getFullYear());
    const mm = m ?? (getParts(endDate)?.m ?? now.getMonth() + 1);
    const dim = daysInMonth(yy, mm);
    const dd = Math.min(d ?? (getParts(endDate)?.d ?? now.getDate()), dim);
    const pad = (n) => String(n).padStart(2, '0');
    setEndDate(`${yy}-${pad(mm)}-${pad(dd)}`);
  };

  // Display helpers: show UTC times converted to Romania time (Europe/Bucharest)
  const parseUtcString = (utcString) => {
    // utcString like "YYYY-MM-DD HH:mm:ss" -> Date in UTC
    if (!utcString) return null;
    const [datePart, timePart] = String(utcString).split(' ');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm, ss] = timePart.split(':').map(Number);
    // construct as UTC
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0));
  };


  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <PageTitle>Downtime - {plant.id}</PageTitle>
          <FormCard>
            <PickerRow>
              <div>
                <FieldLabel>Start date</FieldLabel>
                <Card>
                  {(() => {
                    const parts = getParts(startDate) || { y: years[1], m: now.getMonth() + 1, d: now.getDate() };
                    const dim = daysInMonth(parts.y, parts.m);
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        <Select value={parts.y} onChange={(e) => setStartParts(parseInt(e.target.value, 10), undefined, undefined)}>
                          {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </Select>
                        <Select value={parts.m} onChange={(e) => setStartParts(undefined, parseInt(e.target.value, 10), undefined)}>
                          {months.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </Select>
                        <Select value={Math.min(parts.d, dim)} onChange={(e) => setStartParts(undefined, undefined, parseInt(e.target.value, 10))}>
                          {Array.from({ length: dim }, (_, i) => i + 1).map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </Select>
                      </div>
                    );
                  })()}
                </Card>
              </div>
              <div>
                <FieldLabel>Start time</FieldLabel>
                <Card>
                  <Select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    <option value="" disabled>Select time</option>
                    {timeSlots.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </Select>
                </Card>
              </div>
            </PickerRow>

            <PickerRow>
              <div>
                <FieldLabel>End date</FieldLabel>
                <Card>
                  {(() => {
                    const startP = getParts(startDate) || { y: years[1], m: now.getMonth() + 1, d: now.getDate() };
                    const parts = getParts(endDate) || { y: Math.max(years[0], startP.y), m: now.getMonth() + 1, d: now.getDate() };
                    const dim = daysInMonth(parts.y, parts.m);
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        <Select value={parts.y} onChange={(e) => setEndParts(parseInt(e.target.value, 10), undefined, undefined)}>
                          {years.map((y) => (
                            <option key={y} value={y} disabled={y < startP.y}>{y}</option>
                          ))}
                        </Select>
                        <Select value={parts.m} onChange={(e) => setEndParts(undefined, parseInt(e.target.value, 10), undefined)}>
                          {months.map((m) => (
                            <option key={m.value} value={m.value} disabled={parts.y === startP.y && m.value < startP.m}>{m.label}</option>
                          ))}
                        </Select>
                        <Select value={Math.min(parts.d, dim)} onChange={(e) => setEndParts(undefined, undefined, parseInt(e.target.value, 10))}>
                          {Array.from({ length: dim }, (_, i) => i + 1).map((d) => (
                            <option key={d} value={d} disabled={parts.y === startP.y && parts.m === startP.m && d < startP.d}>{d}</option>
                          ))}
                        </Select>
                      </div>
                    );
                  })()}
                </Card>
              </div>
              <div>
                <FieldLabel>End time</FieldLabel>
                <Card>
                  {(() => {
                    const startP = getParts(startDate);
                    const endP = getParts(endDate);
                    const startT = startTime;
                    const isSameDay = !!(startP && endP && startP.y === endP.y && startP.m === endP.m && startP.d === endP.d);
                    return (
                      <Select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                        <option value="" disabled>Select time</option>
                        {timeSlots.map((t) => (
                          <option key={t} value={t} disabled={isSameDay && startT && t <= startT}>{t}</option>
                        ))}
                      </Select>
                    );
                  })()}
                </Card>
              </div>
            </PickerRow>

            <div>
              <FieldLabel>Inverters Down</FieldLabel>
              <Card>
                <Select 
                  value={invertersDown ?? ''} 
                  onChange={(e) => setInvertersDown(Number(e.target.value))}
                  disabled={loadingInverters}
                >
                  {loadingInverters ? (
                    <option value="">Loading...</option>
                  ) : (
                    <>
                      <option value="" disabled>Select inverters down</option>
                      {Array.from({ length: maxInverters + 1 }, (_, i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </>
                  )}
                </Select>
              </Card>
              {invertersDown !== null && maxInverters > 0 && (
                <div style={{ marginTop: '4px', color: '#333333', fontWeight: 600, fontSize: '0.8rem' }}>
                  Inverters Running: {Math.max(0, maxInverters - invertersDown)} / {maxInverters}
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Timezone</FieldLabel>
              <Card>
                <SegmentedToggle>
                  <SegmentedOption 
                    $active={timezone === 'UTC'} 
                    onClick={() => setTimezone('UTC')}
                  >
                    UTC
                  </SegmentedOption>
                  <SegmentedOption 
                    $active={timezone === 'CET'} 
                    onClick={() => setTimezone('CET')}
                  >
                    CET
                  </SegmentedOption>
                  <SegmentedOption 
                    $active={timezone === 'EET'} 
                    onClick={() => setTimezone('EET')}
                  >
                    EET
                  </SegmentedOption>
                </SegmentedToggle>
              </Card>
            </div>

            <SubmitBar>
              <SubmitButton onClick={handleSubmit} disabled={invalidRange}>Submit Downtime</SubmitButton>
            </SubmitBar>
          </FormCard>

          <DowntimeRecordsList plantId={plant.id} refreshKey={refreshKey} />
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default DowntimeReportPage;


